// src/components/configurator/ComputerCard.tsx
import React, { useState } from "react";
import { useMutation } from "convex/react";
import { Monitor, Laptop, Terminal, Trash2 } from "lucide-react";
import { api } from "../../../convex/_generated/api";
import { Id } from "../../../convex/_generated/dataModel";
import { StatusBadge } from "../common/StatusBadge";

// Define the shape of the computer object expected by this component.
// Non-coders: If you add more properties in the backend schema, declare them here as well.
export interface Computer {
  _id: Id<"computers">;
  name: string;
  platform: "windows" | "macos" | "linux";
  status: "online" | "idle" | "offline";
  online: boolean;
  machineId?: string; // Optional hardware identifier
}

// Props passed down to this component
export interface ComputerCardProps {
  computer: Computer;
}

/**
 * Platform Detail Configurator Map
 * Maps database literals to visual assets and formatted text labels.
 * If you want to change platform display names or default icons, modify them here!
 */
const platformDetails = {
  windows: {
    label: "Windows",
    Icon: Monitor,
  },
  macos: {
    label: "macOS",
    Icon: Laptop,
  },
  linux: {
    label: "Linux",
    Icon: Terminal,
  },
};

/**
 * ComputerCard Component
 * Displays individual paired computers in the user's dashboard.
 * Supports interactive status updates, hover highlighting, and deletion via a confirmation flow.
 */
export const ComputerCard: React.FC<ComputerCardProps> = ({ computer }) => {
  // --- STATE MANAGEMENT ---
  // Tracks card container hover state to apply neon border highlights
  const [isHovered, setIsHovered] = useState(false);
  // Tracks trash button hover state to apply red warning highlights
  const [isDeleteHovered, setIsDeleteHovered] = useState(false);
  // Tracks whether the remove mutation is currently executing in the backend
  const [isDeleting, setIsDeleting] = useState(false);
  // Captures and displays any backend validation/network errors
  const [error, setError] = useState<string | null>(null);

  // Initialize the Convex mutation hook
  const removeComputer = useMutation(api.computers.removeComputer);

  // --- ADJUSTABLE CUSTOM SETTINGS (Non-coder friendly!) ---
  // Feel free to tweak these spacing, size, and timing settings:
  const uiSettings = {
    cardPadding: "1.25rem",
    cardBorderRadius: "var(--border-radius-md)", // Defaults to 8px via index.css
    transitionSpeed: "var(--transition-speed)",   // Defaults to 0.2s via index.css
    platformIconSize: 18,
    deleteIconSize: 18,
  };

  // Safe fallback configuration if database platform string doesn't match keys
  const platformInfo = platformDetails[computer.platform] || {
    label: computer.platform.charAt(0).toUpperCase() + computer.platform.slice(1),
    Icon: Monitor,
  };
  const PlatformIcon = platformInfo.Icon;

  // --- BUTTON CLICK HANDLER ---
  const handleRemove = async () => {
    // 1. Trigger confirmation gate to prevent accidental clicks
    const confirmMessage = `Are you sure you want to remove the computer "${computer.name}"? This will also remove it from any configured layout positions.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    // 2. Set loading states and clear old errors
    setIsDeleting(true);
    setError(null);

    try {
      // 3. Invoke Convex backend mutation
      await removeComputer({ computerId: computer._id });
      // Note: If successful, the parent dashboard query will update reactively 
      // causing this component to unmount, so no need to clear isDeleting state.
    } catch (err) {
      console.error("Error removing computer:", err);
      // 4. Handle errors gracefully by displaying them in the card layout
      const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred. Please try again.";
      setError(errorMessage);
      setIsDeleting(false);
    }
  };

  // --- INLINE REACT STYLES ---
  // Non-coders: You can adjust the colors and layout borders here if you want to tweak dashboard styling.
  const cardStyle: React.CSSProperties = {
    backgroundColor: "var(--color-bg-secondary)",
    border: `1px solid ${isHovered ? "var(--color-accent)" : "var(--color-border)"}`,
    borderRadius: uiSettings.cardBorderRadius,
    padding: uiSettings.cardPadding,
    transition: `border-color ${uiSettings.transitionSpeed} ease, box-shadow ${uiSettings.transitionSpeed} ease`,
    boxShadow: isHovered ? "0 0 12px var(--color-accent-glow)" : "none",
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    position: "relative",
    textAlign: "left",
  };

  const headerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  };

  const platformContainerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    color: "var(--color-text-secondary)",
    fontSize: "0.85rem",
  };

  const removeButtonStyle: React.CSSProperties = {
    background: "transparent",
    border: "none",
    color: isDeleteHovered ? "var(--color-error)" : "var(--color-text-muted)",
    cursor: isDeleting ? "not-allowed" : "pointer",
    padding: "0.4rem",
    borderRadius: "var(--border-radius-sm)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: `color ${uiSettings.transitionSpeed} ease, background-color ${uiSettings.transitionSpeed} ease`,
    backgroundColor: isDeleteHovered ? "rgba(239, 68, 68, 0.1)" : "transparent",
    opacity: isDeleting ? 0.5 : 1,
  };

  const contentStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "0.25rem",
  };

  const nameStyle: React.CSSProperties = {
    fontSize: "1.15rem",
    fontWeight: 600,
    color: "var(--color-text-primary)",
    wordBreak: "break-word",
  };

  const footerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderTop: "1px solid var(--color-border)",
    paddingTop: "0.75rem",
    marginTop: "0.25rem",
  };

  const errorStyle: React.CSSProperties = {
    color: "var(--color-error)",
    fontSize: "0.8rem",
    marginTop: "0.5rem",
    wordBreak: "break-word",
  };

  return (
    <div 
      style={cardStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`computer-card-${computer._id}`}
    >
      {/* Top row: Platform indication details and the remove button */}
      <div style={headerStyle}>
        <div style={platformContainerStyle} data-testid="computer-platform">
          <PlatformIcon size={uiSettings.platformIconSize} aria-hidden="true" />
          <span>{platformInfo.label}</span>
        </div>

        <button
          onClick={handleRemove}
          disabled={isDeleting}
          onMouseEnter={() => setIsDeleteHovered(true)}
          onMouseLeave={() => setIsDeleteHovered(false)}
          style={removeButtonStyle}
          aria-label={`Remove computer ${computer.name}`}
          data-testid="remove-computer-btn"
        >
          {isDeleting ? (
            <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>...</span>
          ) : (
            <Trash2 size={uiSettings.deleteIconSize} aria-hidden="true" />
          )}
        </button>
      </div>

      {/* Middle section: Device display name */}
      <div style={contentStyle}>
        <h3 style={nameStyle} data-testid="computer-name">
          {computer.name}
        </h3>
        {computer.machineId && (
          <span style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }} data-testid="computer-machine-id">
            ID: {computer.machineId}
          </span>
        )}
      </div>

      {/* Bottom section: Live status badge indicator */}
      <div style={footerStyle}>
        <span style={{ fontSize: "0.85rem", color: "var(--color-text-secondary)" }}>
          Status
        </span>
        <StatusBadge status={computer.status} />
      </div>

      {/* Render validation or network errors if any exist */}
      {error && (
        <div style={errorStyle} role="alert" data-testid="computer-card-error">
          {error}
        </div>
      )}
    </div>
  );
};

export default ComputerCard;
